import React, { useRef, useEffect, useImperativeHandle, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import Plyr from 'plyr'
import { useBronson } from '../../features/useBronson'

/**
 * A collection of video resources.
 * @typedef {Array<{path: string, type: ('webm'|'mp4')}>} VideoSource
 */

/**
 * Possible connection type values.
 * @type {Readonly<{"slow-2g": number, "2g": number, "3g": number, "4g": number}>}
 */
export const EffectiveConnectionTypeEnum = Object.freeze({
  'slow-2g': 0,
  '2g': 1,
  '3g': 2,
  '4g': 3,
})

/**
 * Compare the current and the preferred downlink.
 * @return {boolean} - If the current connection matches at least the preferred one.
 */
function checkConnectivity(connection, autoplayMinimumConnection) {
  /**
   * If no preferred connection type was passed we assume a great connection type (4G).
   * @type {string}
   */
  const preferredConnectionType = autoplayMinimumConnection ?? '4g'
  const currentDownlink = EffectiveConnectionTypeEnum[connection?.effectiveType]
  const minDownlink = EffectiveConnectionTypeEnum[preferredConnectionType]

  return currentDownlink < minDownlink
}

/**
 * Bronson Video component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-video.html
 */
export const Video = React.forwardRef(
  /**
   * @param {React.ReactNode} [alt] - Alternative text node for the video.
   * @param {boolean} [autoPlay] - Autoplay the video. Is overridden in some contexts via {@link autoplayMinimumConnection}.
   * @param {EffectiveConnectionTypeEnum} [autoplayMinimumConnection] - Autoplay only when the connection of the client is sufficient. Only supported in Chrome browsers for now.
   * @param {boolean} [captions] - Enable video captions via embedded text track element.
   * @param {string} [captionsLabel] - A user-readable label of the text track.
   * @param {string} [captionsLanguage] - The language of the text track data.
   * @param {string} [captionsSrc] - The path to the `.vtt` caption file, https://developer.mozilla.org/en-US/docs/Web/API/WebVTT_API.
   * @param {boolean} [controls] - Show the video native controls. If set to `false` no controls will be displayed, otherwise the following Plyr controls will be utilized: ['play', 'play-large', 'progress', 'current-time', 'mute', 'volume', 'captions', 'fullscreen'].
   * @param {boolean} [fullscreen] - Allow the video to be played in fullscreen mode.
   * @param {string} [iconUrl] - A custom icon URL for the {@link plyr} SVG sprite.
   * @param {string} [id] - Optional `[id]` attribute value.
   * @param {boolean} [loop] - Play the video in a loop.
   * @param {boolean} [muted] - Mute the video. Essential if {@link autoPlay} is used.
   * @param {function} [onEnded] - Callback function to be called when the video finishes playing.
   * @param {function} [onEnterFullscreen] - Callback function to be called when the video enters fullscreen mode.
   * @param {function} [onExitFullscreen] - Callback function to be called when the video exits fullscreen mode.
   * @param {function} [onPause] - Callback function to be called when the video stops playing.
   * @param {function} [onPlay] - Callback function to be called when the video starts playing.
   * @param {function} [onPlaying] - Callback function to be called when the video is playing.
   * @param {string} [poster] - A URL to the poster image.
   * @param {boolean} [selfHosted] - Whether the video is using a self-hosted video or a {@link service}.
   * @param {('youtube'|'vimeo')} [service] - Opposite of {@link selfHosted}.
   * @param {VideoSource} [src] - A collection of video resources of type {@link VideoSource}.
   * @param {string} [testId] - The `[data-testid]` attribute on the component element.
   * @param {object} [otherProps] - Other props passed to the component element.
   * @returns {JSX.Element} - The Video component.
   * @constructor
   */
  (
    {
      alt,
      autoPlay,
      autoplayMinimumConnection,
      captions,
      captionsLabel,
      captionsLanguage,
      captionsSrc,
      controls,
      fullscreen,
      iconUrl,
      id,
      loop,
      muted,
      onEnded,
      onEnterFullscreen,
      onExitFullscreen,
      onPause,
      onPlay,
      onPlaying,
      poster,
      selfHosted,
      service,
      src,
      testId,
      ...otherProps
    },
    ref
  ) => {
    const { cdn: bronsonCDN } = useBronson()
    const [apiForcedPause, setApiForcedPause] = useState(false)
    const [insufficientConnection, setInsufficientConnection] = useState(false)

    const videoRef = useRef(null)
    const plyrInstance = useRef(null)

    /**
     * Handle play-state of Video depending on if the current page is visible.
     * If the user paused the video via {@link this.apiForcedPause} or
     * the video has not been played yet, do not play it when the
     * page visibility changes to unhidden/visible.
     */
    const handleVisibilityChange = useCallback(() => {
      if (plyrInstance?.current?.media) {
        if (!plyrInstance?.current?.playing) {
          setApiForcedPause(true)
        }

        !document.hidden && !apiForcedPause ? plyrInstance.current.play() : plyrInstance.current.pause()
      }
    }, [plyrInstance, apiForcedPause])

    /**
     * Get the current network information. Only supported via Chromium for now.
     * @type {NetworkInformation|*}
     */
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

    /**
     * Get the `prefers-reduced-motion` CSS media query setting.
     * @type {MediaQueryList}
     */
    const reduceMotionMediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)')
    const prefersReducedMotion = reduceMotionMediaQueryList?.matches

    /**
     * Check the page visibility and stop/resume the video on `visibilitychange` event.
     */
    document.addEventListener('visibilitychange', handleVisibilityChange)

    useEffect(() => {
      if (autoplayMinimumConnection && !prefersReducedMotion) {
        /**
         * Check if the current connection is insufficient and pause the video.
         * Otherwise check if an autoplay was preferred and play it.
         */
        setInsufficientConnection(connection ? checkConnectivity(connection, autoplayMinimumConnection) : false)
      }
    }, [autoplayMinimumConnection, connection, prefersReducedMotion])

    /**
     * NOTE: Refs and imperative handling are here intended as API for media playback is forwarded to parent
     * state resides within Plyr player and is not mapped to another state object copy.
     */
    useImperativeHandle(ref, () => ({
      play: () => {
        plyrInstance.current.play()
      },
      pause: () => {
        plyrInstance.current.pause()
      },
      stop: () => {
        plyrInstance.current.stop()
      },
      fullscreen: () => {
        plyrInstance.current.fullscreen.enter()
      },
    }))

    useEffect(() => {
      if (!videoRef.current) return undefined

      const plyrConfig = {
        debug: false,
        autoPlay: !insufficientConnection && autoPlay ? true : null,
        invertTime: false,
        controls:
          controls === false
            ? null
            : ['play', 'play-large', 'progress', 'current-time', 'mute', 'volume', 'captions', 'fullscreen'],
        fullscreen: fullscreen ?? {
          fallback: true,
        },
        youtube: {
          noCookie: true,
        },
        urls: {
          youtube: {
            /**
             * Disable YouTube API (for title/aspect ratio calculation)
             * calls through third-party Noembed service.
             * @see https://noembed.com/
             */
            api: '',
          },
        },
        /**
         * Make Plyr respect `[muted]`.
         * @see https://github.com/sampotts/plyr/issues/1495#issuecomment-659735226
         * @type {{enabled: boolean}}
         */
        muted: muted ? { enabled: true } : {},
        storage: muted ? { enabled: false } : {},
        volume: muted ? 0 : null,
        iconUrl: iconUrl || (bronsonCDN ? `${bronsonCDN}/svg/plyr-sprite.svg` : null),
      }

      if (!plyrInstance.current) {
        plyrInstance.current = new Plyr(videoRef.current, plyrConfig)
        onPlaying && plyrInstance.current.on('playing', (event) => onPlaying(event.detail.plyr))
        onPlay && plyrInstance.current.on('play', (event) => onPlay(event.detail.plyr))
        onPause && plyrInstance.current.on('pause', (event) => onPause(event.detail.plyr))
        onEnded && plyrInstance.current.on('ended', (event) => onEnded(event.detail.plyr))
        onEnterFullscreen && plyrInstance.current.on('enterfullscreen', (event) => onEnterFullscreen(event.detail.plyr))
        onExitFullscreen && plyrInstance.current.on('exitfullscreen', (event) => onExitFullscreen(event.detail.plyr))
      }
      const instance = plyrInstance.current
      return () => {
        instance.destroy()
      }
    }, [
      videoRef,
      autoPlay,
      onPlaying,
      onPlay,
      onPause,
      onEnded,
      onEnterFullscreen,
      onExitFullscreen,
      iconUrl,
      bronsonCDN,
      muted,
      controls,
      fullscreen,
      insufficientConnection,
    ])

    /**
     * Assert media sources for the `<video>` element.
     * @returns {unknown[]|null}
     */
    const videoSources = () => {
      if (Array.isArray(src)) {
        return src.map(({ path, type }) => <source key={`${type}${path}`} type={`video/${type}`} src={path} />)
      }
      /**
       * @DEPRECATED
       * @TODO: Remove the legacy VideoSource format in v6.0.0.
       */
      if (src) {
        return Object.entries(src).map((entry) => (
          <source key={`${entry[0]}${entry[1]}`} type={entry[0]} src={entry[1]} />
        ))
      }
      return null
    }

    const renderTrack = () =>
      captions && <track kind="captions" label={captionsLabel} src={captionsSrc} srcLang={captionsLanguage} default />

    /**
     * NOTE: Need to wrap a `<div>` around as Plyr is manipulating complete DOM and React is otherwise not able to unmount.
     */
    return (
      <div>
        {selfHosted ? (
          <video
            ref={videoRef}
            poster={poster}
            className="js-video"
            controls={controls}
            autoPlay={!insufficientConnection && autoPlay ? true : null}
            loop={loop}
            muted
            data-testid={testId}
            {...otherProps}
          >
            {videoSources()}
            {renderTrack()}
            {alt}
          </video>
        ) : (
          <div
            ref={videoRef}
            data-plyr-provider={service}
            data-plyr-embed-id={id}
            className="js-video"
            data-testid={testId}
            {...otherProps}
          />
        )}
      </div>
    )
  }
)

Video.propTypes = {
  alt: PropTypes.node,
  autoPlay: PropTypes.bool, // Bronson template: 'video-has-autoplay'.
  autoplayMinimumConnection: PropTypes.oneOf(Object.keys(EffectiveConnectionTypeEnum)), // Bronson template: 'video-autoplay-minimum-connection'.
  captions: PropTypes.bool, // Bronson template: 'video-has-captions'.
  captionsLabel: PropTypes.string, // Bronson template: 'video-captions-language'.
  captionsLanguage: PropTypes.string, // Bronson template: 'video-captions-shortcut'.
  captionsSrc: PropTypes.string, // Bronson template: 'video-has-autoplay'.
  controls: PropTypes.bool,
  fullscreen: PropTypes.bool,
  iconUrl: PropTypes.string,
  id: PropTypes.string, // Bronson template: 'video-id'.
  loop: PropTypes.bool, // Bronson template: 'video-has-loop'.
  muted: PropTypes.bool,
  onEnded: PropTypes.func, // Event callback when media completes. @see https://github.com/sampotts/plyr
  onEnterFullscreen: PropTypes.func, // Event callback when fullscreen is entered. @see https://github.com/sampotts/plyr
  onExitFullscreen: PropTypes.func, // Event callback when exiting fullscreen. @see https://github.com/sampotts/plyr
  onPause: PropTypes.func, // Event callback when media is paused. @see https://github.com/sampotts/plyr
  onPlay: PropTypes.func, // Event callback when media begins to play after being paused. @see https://github.com/sampotts/plyr
  onPlaying: PropTypes.func, // Event callback when media is playing. @see https://github.com/sampotts/plyr
  poster: PropTypes.string, // Bronson template: 'video-poster'.
  selfHosted: PropTypes.bool, // Bronson template: 'video-is-self-hosted'.
  service: PropTypes.oneOf(['youtube', 'vimeo']), // Bronson template: 'video-service'.
  // @TODO: Bug in `React.forwardRef`, need to revise this at later point.
  // src: PropTypes.arrayOf(PropTypes.shape({ path: PropTypes.string, type: PropTypes.oneOf(['webm', 'mp4']) })), // Bronson template: 'video-src'.
  // eslint-disable-next-line react/forbid-prop-types
  src: PropTypes.any, // Bronson template: 'video-src'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}
